import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  CardActions,
  Alert as MuiAlert,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { PIPELINE_STATUS, RUN_STATUS } from '../utils/constants';
import { useAuth } from '../store/authStore';
import api from '../utils/api';

const REFRESH_INTERVAL_MS = 30000; // 30 seconds

const DashboardPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    recentPipelines: [],
    recentRuns: [],
    activeAlerts: 0,
    stats: {
      totalPipelines: 0,
      activePipelines: 0,
      successfulRuns: 0,
      failedRuns: 0,
      dailyRuns: [],
    },
  });

  const { isAuthenticated } = useAuth();
  const canFetch = isAuthenticated;
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/stats/dashboard');
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to load dashboard data. Retrying...');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (canFetch) {
      fetchDashboardData(true);
    }
  }, [canFetch, fetchDashboardData]);

  // Auto-refresh polling
  useEffect(() => {
    if (!canFetch) return;
    const interval = setInterval(() => fetchDashboardData(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [canFetch, fetchDashboardData]);

  const handleCreatePipeline = () => {
    navigate('/pipelines/new');
  };

  const handleViewPipeline = (id) => {
    navigate(`/pipelines/${id}`);
  };

  const handleViewRun = (id) => {
    navigate(`/runs/${id}`);
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const getStatusChip = (status) => {
    const statusProps = {
      [RUN_STATUS.PENDING]: { label: 'Pending', color: 'warning' },
      [RUN_STATUS.RUNNING]: { label: 'Running', color: 'info' },
      [RUN_STATUS.COMPLETED]: { label: 'Completed', color: 'success' },
      [RUN_STATUS.FAILED]: { label: 'Failed', color: 'error' },
      [RUN_STATUS.CANCELLED]: { label: 'Cancelled', color: 'default' },
      [PIPELINE_STATUS.DRAFT]: { label: 'Draft', color: 'default' },
      [PIPELINE_STATUS.ACTIVE]: { label: 'Active', color: 'success' },
      [PIPELINE_STATUS.PAUSED]: { label: 'Paused', color: 'warning' },
      [PIPELINE_STATUS.ARCHIVED]: { label: 'Archived', color: 'default' },
    };

    const { label, color } = statusProps[status] || { label: status, color: 'default' };
    return <Chip label={label} color={color} size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  const COLORS = ['#0088FE', '#FF8042'];

  const totalRuns = dashboardData.stats.successfulRuns + dashboardData.stats.failedRuns;
  const pieData = [
    { name: 'Successful', value: dashboardData.stats.successfulRuns },
    { name: 'Failed', value: dashboardData.stats.failedRuns },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Box>
          <IconButton onClick={handleRefresh} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreatePipeline}>
            Create Pipeline
          </Button>
        </Box>
      </Box>

      {error && (
        <MuiAlert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
            <Typography variant="h6" color="text.secondary">Total Pipelines</Typography>
            <Typography variant="h3" component="div" sx={{ mt: 2, mb: 1 }}>
              {dashboardData.stats.totalPipelines}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dashboardData.stats.activePipelines} active
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
            <Typography variant="h6" color="text.secondary">Successful Runs</Typography>
            <Typography variant="h3" component="div" sx={{ mt: 2, mb: 1, color: 'success.main' }}>
              {dashboardData.stats.successfulRuns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalRuns > 0 ? Math.round((dashboardData.stats.successfulRuns / totalRuns) * 100) : 0}% success rate
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
            <Typography variant="h6" color="text.secondary">Failed Runs</Typography>
            <Typography variant="h3" component="div" sx={{ mt: 2, mb: 1, color: 'error.main' }}>
              {dashboardData.stats.failedRuns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalRuns > 0 ? Math.round((dashboardData.stats.failedRuns / totalRuns) * 100) : 0}% failure rate
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
            <Typography variant="h6" color="text.secondary">Active Alerts</Typography>
            <Typography variant="h3" component="div" sx={{ mt: 2, mb: 1, color: 'warning.main' }}>
              {dashboardData.activeAlerts}
            </Typography>
            <Button variant="text" size="small" onClick={() => navigate('/alerts')}>
              View Alerts
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" gutterBottom>Pipeline Runs (Last 7 Days)</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={dashboardData.stats.dailyRuns} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="successful" fill="#52c41a" stackId="a" />
                <Bar dataKey="failed" fill="#ff4d4f" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" gutterBottom>Run Success Rate</Typography>
            <ResponsiveContainer width="100%" height="80%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <Typography variant="body2" align="center" color="text.secondary">
              Total runs: {totalRuns}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Items */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Recent Pipelines</Typography>
            <Divider sx={{ mb: 2 }} />
            {dashboardData.recentPipelines.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No pipelines yet. Create your first pipeline to get started.
              </Typography>
            ) : (
              dashboardData.recentPipelines.map((pipeline) => (
                <Card key={pipeline.id} sx={{ mb: 2 }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" component="div">{pipeline.name}</Typography>
                      {getStatusChip(pipeline.status)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Last run: {formatDate(pipeline.lastRun)}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => handleViewPipeline(pipeline.id)}>View</Button>
                    {pipeline.status === PIPELINE_STATUS.ACTIVE && (
                      <Button size="small" startIcon={<PlayIcon />} onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.post('/runs/', { pipeline_id: pipeline.id });
                          navigate(`/runs?pipeline_id=${pipeline.id}`);
                        } catch (err) {
                          console.error('Error running pipeline:', err);
                        }
                      }}>
                        Run
                      </Button>
                    )}
                  </CardActions>
                </Card>
              ))
            )}
            <Button variant="text" fullWidth onClick={() => navigate('/pipelines')} sx={{ mt: 1 }}>
              View All Pipelines
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Recent Runs</Typography>
            <Divider sx={{ mb: 2 }} />
            {dashboardData.recentRuns.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No pipeline runs yet.
              </Typography>
            ) : (
              dashboardData.recentRuns.map((run) => (
                <Card key={run.id} sx={{ mb: 2 }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" component="div">{run.pipelineName}</Typography>
                      {getStatusChip(run.status)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Started: {formatDate(run.startedAt)} {run.duration > 0 && `\u2022 Duration: ${formatDuration(run.duration)}`}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => handleViewRun(run.id)}>View Details</Button>
                  </CardActions>
                </Card>
              ))
            )}
            <Button variant="text" fullWidth onClick={() => navigate('/runs')} sx={{ mt: 1 }}>
              View All Runs
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
